
// PDS - Persistent data system
import { conversationService, mapStateService } from '../services/dataPersistence';


import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Compass } from 'lucide-react';
import { Message, Location } from '../types/chat'; 
import { ChatMessage } from './ChatMessage';
import { ProcessingIndicator } from './ProcessingIndicator';
import { QuickActionsPanel } from './QuickActionsPanel';
import { ImageLocationSearch } from './ImageLocationSearch';
import { TripDetails, Itinerary } from '../types/itinerary';
import { Plus, Mic,  X  } from 'lucide-react';
import InlineStatusIndicator from './ui/InlineStatusIndicator';

import { useIsMobile } from '../hooks/useIsMobile'; // Add this import

import { parseAssistantMessage } from '../utils/MessageParser';
//import { validateQuery } from '../utils/queryValidator';

//import { generateItinerary } from '../services/itinerary/builder';

interface ChatPanelProps {
  messages: Message[];
  onSendMessage: (content: string) => void;
  isLoading: boolean;
  onLocationSelect: (location: Location | null) => void;
  onLocationsUpdate: (locations: Location[]) => void;
  streamingMessage: Message | null;
  selectedLocation: Location | null;
  error: string | null;
  weatherLocation: string | null;
  onImageSearch: (images: File[]) => void;
  isProcessingImages: boolean;
  onItineraryUpdate?: (itinerary: Partial<Itinerary>, isStreaming?: boolean) => void;
  onPlannerReset?: () => void;
}

export function ChatPanel({
  messages,
  onSendMessage,
  isLoading,
  onLocationSelect,
  streamingMessage,
  selectedLocation,
  onLocationsUpdate,
  error,
  weatherLocation,
  onImageSearch,
  isProcessingImages,
  onItineraryUpdate,
  onPlannerReset
}: ChatPanelProps) {

  const isMobile = useIsMobile(); // Add this line


  const [validationError, setValidationError] = useState<string | null>(null);

  const [input, setInput] = useState('');
  const [showProcessing, setShowProcessing] = useState(false);
  const [showImageSearch, setShowImageSearch] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [helpPromptsVisible, setHelpPromptsVisible] = useState(false);



  // PDS - Location refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const inputLineCount = input.split('\n').length;
  const inputHeight = Math.min(40 + (inputLineCount - 1) * 20, 120);
  const forbiddenChars = /[{}<>\[\]`|\\~^]/;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Track previous streaming state
  const wasStreamingRef = useRef(false);


  //Add this useEffect to handle click outside:
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.quick-actions-container') && 
          !target.closest('.quick-actions-trigger')) {
        setShowQuickActions(false);
      }
    };

    if (showQuickActions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showQuickActions]);


  useEffect(() => {
    const isCurrentlyStreaming = !!streamingMessage || isLoading;
    
    // If we were streaming and now we're not
    if (wasStreamingRef.current && !isCurrentlyStreaming) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
    
    wasStreamingRef.current = isCurrentlyStreaming;
  }, [streamingMessage, isLoading]);

  // Simplify the rebuild event handler
  useEffect(() => {
    const handleRebuildComplete = () => {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    };
    
    window.addEventListener('tripper-rebuild-complete', handleRebuildComplete);
    return () => window.removeEventListener('tripper-rebuild-complete', handleRebuildComplete);
  }, []);

  // PDS - useEffect for scroll position

  const handleSubmit_UNUSED = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      onSendMessage(input);
      setInput('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = '38px';
      }
    }
  };

  // Update handleSubmit function
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (input.trim() && !isLoading) {
      const { isValid } = validateSafeInput(input);
      
      if (!isValid) {
        // Set error message
        setValidationError('Invalid content detected');
        
        // Clear input
        setInput('');
        if (textareaRef.current) {
          textareaRef.current.style.height = '38px';
        }
        
        // Clear error after 3 seconds
        setTimeout(() => {
          setValidationError(null);
        }, 3000);
        
        return;
      }
      
      // Clear any existing error
      setValidationError(null);
      
      onSendMessage(input);
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = '38px';
      }
    }
  };

  useEffect(() => {
    // Only scroll when messages change and we're not loading
    if (messagesEndRef.current && messages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'end'
        });
      }, 100);
    }
  }, [messages, streamingMessage?.content]);


  // PDS - Add this function to save scroll position

  // 1. For explicitly requested location cards (from a message like "Show me attractions")
  // These should go in the conversation tree
  const handleExplicitLocationRequest = (location, messageId) => {
    if (!location) return;
    
    console.log("[CHAT PANEL SHARING LOCATIONS ] --- Location coordinates debug:", {
      original: location,
      position: location.position,
      coordinates: location.coordinates,
      lat: location.lat,
      latitude: location.latitude
    });

    // Only add location cards to the conversation tree when they're explicitly requested
    conversationService.addLocationCard(
      {
        id: location.id,
        name: location.name,
        city: location.city,
        country: location.country,
        position: location.position,
        rating: location.rating,
        reviews: location.reviews,
        description: location.description || ''
      },
      messageId
    );
  };

  // 2. For general map navigation and location selection
  // These should only update the map state, not the conversation tree
  const handleLocationSelect = (location) => {
    // Don't add to conversation tree, just call the original handler
    onLocationSelect(location);
    
    // Update map state
    if (location) {
      mapStateService.setSelectedLocation(location.id);
    } else {
      mapStateService.setSelectedLocation(null);
    }
  };

  // 3. For handling multiple locations update
  const handleLocationsUpdate = (locations) => {
    // Call the original handler
    onLocationsUpdate(locations);
    
    // Just update visible locations in map state
    mapStateService.updateVisibleLocations(
      locations.map(location => location.id)
    );
  }


  const handleScroll = () => {
    if (chatContainerRef.current) {
      conversationService.saveScrollPosition(chatContainerRef.current.scrollTop);
    }
  };

  const handleQuickAction = (action: string) => {

     if (action.startsWith('HELPER_PROMPT:')) {
      const template = action.replace('HELPER_PROMPT:', '');
      setInput(template);
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(template.length, template.length);
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
      }
      return;
    }
    
    console.log("[CHAT PANEL] Quick action triggered:", action);

    switch (action) {
      case 'find':
        setShowImageSearch(true); // Opens image search modal
        break;
      case 'plan':
        onItineraryUpdate?.({
          tripDetails: {
            destination: '',
            travelGroup: ''
          },
          days: []
        });
        onPlannerReset?.();

        break;
      case 'weather':
        onSendMessage('What\'s the weather like?');
        break;
      case 'help':
        onSendMessage('help');
        break;
      default:
        onSendMessage(action);
        break;
    }  
  };


  // Smarter validation that allows normal text but blocks JSON/code
  const validateSafeInput = (text: string): { isValid: boolean; sanitized: string } => {
    // Check for actual JSON patterns (not just any brackets)
    const jsonPatterns = [
      /\{\s*"[^"]+"\s*:/,                    // {"key":
      /\[\s*\{/,                             // [{ array of objects
      /\}\s*,\s*\{/,                         // }, { object separator
      /"[^"]+"\s*:\s*(\[|{|"|'|\d|true|false|null)/, // Full JSON property
      /\[\s*\d+\s*,\s*\d+\s*(,\s*\d+)*\s*\]/, // Coordinate arrays [1,2,3]
      /^\s*\{[\s\S]*\}\s*$/,                 // Entire message is JSON object
      /^\s*\[[\s\S]*\]\s*$/,                 // Entire message is JSON array
    ];
    
    // Check for code patterns
    const codePatterns = [
      /<script|<iframe|javascript:/i,        // Script injection
      /function\s*\(|=>\s*\{/,              // Function definitions
      /eval\s*\(|setTimeout\s*\(/,          // Dangerous functions
      /\$\{[^}]+\}/,                        // Template literals
      /`;.*`;/,                             // Multi-line code blocks
    ];
    
    // Check all patterns
    for (const pattern of [...jsonPatterns, ...codePatterns]) {
      if (pattern.test(text)) {
        return { isValid: false, sanitized: '' };
      }
    }
    
    return { isValid: true, sanitized: text };
  };

  // Safe display function
  const sanitizeForDisplay = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text; // This automatically escapes HTML
    return div.innerHTML;
  };

  const handleTripPlannerSubmit = async (details: TripDetails) => {
    try {
      // Start with a loading state
      onItineraryUpdate?.({
        tripDetails: {
          destination: details.destination,
          startDate: details.startDate?.toISOString(),
          endDate: details.endDate?.toISOString(),
          travelGroup: details.travelGroup
        }
      });

      // Generate the itinerary message
      const message = `I'm planning a trip to ${details.destination} ${
        details.startDate && details.endDate 
          ? `from ${details.startDate.toLocaleDateString()} to ${details.endDate.toLocaleDateString()}` 
          : ''
      }. I'm traveling as a ${details.travelGroup}${
        details.preferences?.activityTypes?.length 
          ? `. I'm interested in: ${details.preferences.activityTypes.join(', ')}`
          : ''
      }. Can you suggest an itinerary?`;

      onSendMessage(message);
    } catch (error) {
      console.error('Error generating itinerary:', error);
    }
  };

  return (
  <div className="flex flex-col h-full bg-white">
    {/* Gray background with padding and gap */}
       <div 
          className="flex flex-col h-full mx-auto w-full" 
          style={{ 
            //padding: 'var(--chat-outer-padding)',
            //gap: 'var(--chat-outer-padding)',
            maxWidth: 'var(--chat-content-max-width, 500px)',
            //overflowX: 'visible'  // Add this
            // style={{ overflowX: 'visible' }}
          }}
        >
        {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div 
          className="flex items-center justify-between"
          style={{ 
            padding: 'var(--chat-header-padding)'
          }}
        >
          <div className="flex items-center gap-5">
            <div>
              
              {/* <h2 className="font-bold text-gray-900" style={{ fontSize: 'var(--chat-header-size)' }}>TrippyMate©</h2> */}
              <h2 className="font-bold text-gray-900" style={{ fontSize: 'var(--chat-header-size)' }}>TrippyMate™</h2>
              <p className="text-gray-500" style={{ fontSize: 'var(--chat-subheader-size)' }}>Your AI travel buddy </p>
            </div>
          </div>
          <InlineStatusIndicator />
        </div>
      </div>
        {/* Messages */}
        <div className="flex-1 bg-white rounded-lg shadow-sm min-h-0"
          style={{ 
            touchAction: 'pan-y',  // ADD THIS - only allow vertical scrolling
            //overflowX: 'visible',    // ADD THIS as backup
            overflowY: 'visible'
          }}> {/* Separate white container */}
          <div 
              ref={chatContainerRef}
              className="h-full overflow-y-auto relative z-5001 chat-container"
              /* style={{ 
                paddingTop: '0',      // Remove all padding
                paddingLeft: '0',     // Remove all padding
                paddingRight: '0',    // Remove all padding
                paddingBottom: '30px', // Keep only bottom for scroll
                scrollbarGutter: 'stable both-edges', // This prevents scrollbar from overlapping content
                overflowX: 'hidden',  // ADD THIS
                overscrollBehaviorX: 'none'  // ADD THIS to prevent elastic scrolling
              }} */
              style={{ 
                paddingTop: 'var(--chat-messages-padding)',
                paddingLeft: 'var(--chat-messages-padding)',
                paddingRight: '0px',
                paddingBottom: 'var(--chat-messages-padding-bottom)',
                scrollbarGutter: 'stable', // This prevents scrollbar from overlapping content
                overflowX: 'visible',  // ADD THIS
                overscrollBehaviorX: 'none'  // ADD THIS to prevent elastic scrolling
              }}
              onScroll={(e) => {
                handleScroll();
                // Add scrolling class for scrollbar visibility
                const element = e.target as HTMLElement;
                element.classList.add('scrolling');
                clearTimeout((window as any).scrollEndTimer);
                (window as any).scrollEndTimer = setTimeout(() => {
                  element.classList.remove('scrolling');
                }, 1000);
              }}
            >
            <style>{`
              /* Custom scrollbar with fade effect */
              .chat-container::-webkit-scrollbar {
                width: 10px;
              }
              .chat-container::-webkit-scrollbar-thumb {
                background-color: rgba(0,0,0,0.1);
                border-radius: 10px;
                border: 2px solid white;
                background-clip: padding-box;
                transition: opacity 0.3s;
              }
              .chat-container:not(.scrolling)::-webkit-scrollbar-thumb {
                background-color: rgba(0,0,0,0);
              }
              .chat-container.scrolling::-webkit-scrollbar-thumb {
                background-color: rgba(0,0,0,0.15);
              }
              /* Animation styles */
              @keyframes pulse-border {
                0%, 100% { 
                  border-color: #fca5a5;
                  box-shadow: 0 0 0 2px #fca5a5;
                }
                50% { 
                  border-color:rgb(251, 144, 144);
                  box-shadow: 0 0 0 2px #dc2626;
                }
              }
              textarea.warning-border {
                animation: pulse-border 0.7s ease-in-out infinite !important;
              }
              textarea.warning-border:focus {
                outline: none !important;
              }
              textarea.error-border {
                border-color: #ef4444 !important;
                box-shadow: 0 0 0 2px #ef4444 !important;
              }
              textarea.error-border:focus {
                outline: none !important;
              }
            `}</style>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--chat-message-spacing)' }}>
                {messages.map((message, index) => {
                  const prevMessage = messages[index - 1];
                  const nextMessage = messages[index + 1];
                  
                  // Check if this message is part of a continuous bot sequence
                  const isContinuation = 
                    message.sender === 'bot' && 
                    prevMessage?.sender === 'bot' &&
                    // Check if messages are close in time (within 3 seconds)
                    (message.timestamp.getTime() - prevMessage.timestamp.getTime()) < 10000;
                  
                  // Check if this is the last in a bot sequence
                  const isLastInSequence = 
                    message.sender === 'bot' && 
                    (!nextMessage || 
                    nextMessage.sender !== 'bot' ||
                    (nextMessage.timestamp.getTime() - message.timestamp.getTime()) >= 10000);
                  
                  return (
                    <ChatMessage 
                      key={`${message.id}_${index}`}  
                      message={message} 
                      onLocationSelect={handleLocationSelect}
                      onLocationsUpdate={handleLocationsUpdate}
                      selectedLocation={selectedLocation}
                      isStreaming={streamingMessage?.id === message.id}
                      onActionClick={handleQuickAction}
                      isContinuation={isContinuation}
                      isLastInSequence={isLastInSequence}
                    />
                  );
                })}
                {showProcessing && <ProcessingIndicator isVisible={true} />}
                {error && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-lg">
                    {error}
                  </div>
                )}
              <div ref={messagesEndRef} />
            </div>

            {/* <div ref={messagesEndRef} className="h-1" />  */}
          </div>
        </div>
        {/* Add this CSS to your component or global styles */}
        <style>{`
          @keyframes pulse-border {
            0%, 100% { 
              border-color: #fca5a5; /* red-300 - lighter */
              box-shadow: 0 0 0 1.5px #fca5a5; /* thicker shadow */
            }
            50% { 
              border-color: #dc2626; /* red-600 - darker */
              box-shadow: 0 0 0 1.5px #dc2626;
            }
          }
          textarea.warning-border {
            animation: pulse-border 0.7s ease-in-out infinite !important; /* faster: 1s → 0.7s */
          }
          textarea.warning-border:focus {
            outline: none !important;
            border-color: #ef4444 !important;
          }
          textarea.error-border {
            border-color: #ef4444 !important;
            box-shadow: 0 0 0 1px #ef4444 !important;
          }
          textarea.error-border:focus {
            outline: none !important;
            border-color: #ef4444 !important;
            box-shadow: 0 0 0 1px #dc2626 !important;
          }
        `}</style>
      {/* Input Section */}
      <div className="bg-white shadow-sm border-t border-gray-200 relative py-2">
        {/* Quick Actions Flyout - positioned relative to this container */}
        <QuickActionsPanel 
          onActionClick={handleQuickAction} 
          helpPromptsVisible={helpPromptsVisible}
          isOpen={showQuickActions}
          onClose={() => setShowQuickActions(false)}
          isMobile={isMobile}  // Add this prop
        />
        
        {/* Modern styled input container */}
        <form onSubmit={handleSubmit} className="px-4 pb-2 pt-4">
          <div className={`relative flex items-center rounded-3xl duration-200 px-2 py-1 bg-gray-100 transition-all
            focus-within:ring-2 focus-within:ring-black focus-within:ring-opacity-50
            ${input.length >= 500 ? 'error-border ring-2 ring-red-500' : input.length >= 400 ? 'warning-border' : ''}`}
            style={{
              minHeight: '48px'
            }}>
            {!isMobile && (
              <button
                type="button"
                className={`self-center p-2 rounded-full transition-all duration-200 ${
                  showQuickActions ? 'bg-gray-200 rotate-45' : 'hover:bg-gray-200'
                }`}
                onClick={() => setShowQuickActions(!showQuickActions)}
              >
                <Plus size={20} className="text-gray-600" />
              </button>
            )}
            {/* <button
              type="button"
              className={`self-center p-2 rounded-full transition-all duration-200 ${
                showQuickActions 
                  ? 'bg-gray-200 rotate-45' 
                  : 'hover:bg-gray-200'
              }`}
              onClick={() => setShowQuickActions(!showQuickActions)}
              >
              <Plus size={20} className="text-gray-600 transition-transform duration-200" />
            </button> */}
            
            {/* Input field */}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue.length <= 500) {
                  if (validationError) setValidationError(null);
                  setInput(newValue);
                  // Auto-resize
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                
                const pastedText = e.clipboardData.getData('text');
                
                // Check for forbidden characters
                if (forbiddenChars.test(pastedText)) {
                  setValidationError('Cannot paste content with special characters');
                  setTimeout(() => setValidationError(null), 3000);
                  return;
                }
                
                // Check for JSON/code patterns
                const { isValid } = validateSafeInput(pastedText);
                if (!isValid) {
                  setValidationError('Cannot paste code or structured data');
                  setTimeout(() => setValidationError(null), 3000);
                  return;
                }
                
                // Calculate how much can be pasted
                const currentText = input;
                const cursorStart = e.currentTarget.selectionStart || 0;
                const cursorEnd = e.currentTarget.selectionEnd || 0;
                const textBeforeCursor = currentText.slice(0, cursorStart);
                const textAfterCursor = currentText.slice(cursorEnd);
                const availableSpace = 500 - textBeforeCursor.length - textAfterCursor.length;
                
                if (availableSpace <= 0) {
                  setValidationError('Character limit reached');
                  setTimeout(() => setValidationError(null), 2000);
                  return;
                }
                
                // Truncate pasted text if needed
                const textToPaste = pastedText.slice(0, availableSpace);
                const newText = textBeforeCursor + textToPaste + textAfterCursor;
                
                setInput(newText);
                // Show warning if text was truncated

                if (pastedText.length > availableSpace) {
                  setValidationError('Max character limit');
                  setTimeout(() => setValidationError(null), 3000);
                }
                // Auto-resize
                setTimeout(() => {
                  if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                    textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
                  }
                }, 0);

              }}
              onKeyDown={(e) => {
                // Block forbidden characters
                if (forbiddenChars.test(e.key)) {
                  e.preventDefault();
                  setValidationError('Character not allowed');
                  setTimeout(() => setValidationError(null), 2000);
                  return;
                }
               if (e.key === 'Enter' && !e.shiftKey) { // Add shift check for multiline
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              onFocus={() => setShowQuickActions(false)} // Close flyout when input is focused
              placeholder="Ask anything..."
                className={`flex-1 px-3 py-2 outline-none border-none focus:outline-none focus:ring-0 text-gray-700 placeholder-gray-400 bg-transparent resize-none overflow-y-auto
                ${input.length >= 500 ? 'text-red-500' : ''}`} // Simplified - text color only at 500
              style={{ 
                fontSize: 'var(--chat-font-size, 14px)',
                fontWeight: 'var(--chat-font-weight, 400)',
                lineHeight: 'var(--chat-line-height, 1.5)',
                minHeight: '24px',
                maxHeight: '120px'
              }}
              rows={1}
              disabled={isLoading}
              maxLength={500}
              autoComplete="off"
              spellCheck="true"
            />
            
            {/* Character count indicator - only show when getting close to limit */}
            {input.length > 350 && (
              <span className={`text-xs mr-2 ${input.length >= 500 ? 'text-red-500' : input.length >= 400 ? 'text-orange-500' : 'text-gray-400'}`}>
                {input.length}/500
              </span>
            )}

            {/* Clear button - only show when there's text */}
            {input.length > 0 && (
              <button
                type="button"
                className="self-center p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-all mr-1"
                onClick={() => {
                  setInput('');
                  if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                    textareaRef.current.focus();
                  }
                }}
                >
                <X size={14} className="text-gray-400 opacity-70 hover:opacity-100 transition-opacity" />
              </button>
            )}
            {/* Mic button - placeholder for future voice input */}
            {/* <button
              type="button"
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
              onClick={() => {
                // Future: Implement voice input
                setValidationError('Voice input coming soon!');
                setTimeout(() => setValidationError(null), 2000);
              }}
            >
              <Mic size={20} className="text-gray-600" />
            </button> */}
            
            {/* Send button - add self-center */}
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="self-center p-2 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 
                <Loader2 size={20} className="animate-spin text-gray-600" /> : 
                <Send size={20} className="text-gray-600" />
              }
            </button>
          </div>
          
          {/* Error message */}
          {validationError && (
            <div className="mt-2 px-4">
              <p className="text-xs text-red-500 flex items-center gap-1">
                <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {validationError}
              </p>
            </div>
          )}
        </form>
        
        {/* Disclaimer */}
        <p className="text-xs text-gray-500 text-center py-2">
          ⓘ Trippy can make mistakes. Verify key information.
        </p>
      </div>
      {/* Image Search Modal */}
      {showImageSearch && (
        <ImageLocationSearch
          onSubmit={onImageSearch}
          onClose={() => setShowImageSearch(false)}
          isProcessing={isProcessingImages}
        />
      )}
      {isMobile && (
        <button
          type="button"
          className={`fixed bottom-24 left-4 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg transition-all duration-200 z-[5500] flex items-center justify-center ${
            showQuickActions ? 'rotate-45' : ''
          }`}
          onClick={() => setShowQuickActions(!showQuickActions)}
        >
          <Plus size={24} />
        </button>
      )}
     </div>
      
  </div>    
  );
}